import React, { useState, useEffect } from 'react';
import { fetchCollection, getOptimizedImageUrl } from '../lib/cms';
import GalleryModal from './GalleryModal';

export default function GallerySection({
  gallerySlug = 'ibjerget',
  title = 'Stemningsgalleri fra Ibjerget',
  subtitle = 'Ro, natur og nærværende øjeblikke på Midtfyn',
  customImages = null,
  limit = 6
}) {
  const [images, setImages] = useState(customImages || []);
  const [galleryInfo, setGalleryInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(!customImages);

  useEffect(() => {
    if (customImages && customImages.length > 0) {
      setImages(customImages);
      setLoading(false);
      return;
    }

    let isMounted = true;
    async function loadGallery() {
      try {
        const res = await fetchCollection('galleries');
        if (res && res.items && res.items.length > 0) {
          // Find by title or slug
          const match = res.items.find(
            (g) =>
              (g.title && g.title.toLowerCase() === gallerySlug.toLowerCase()) ||
              (g.slug && (g.slug._ === gallerySlug || g.slug === gallerySlug))
          ) || res.items[0];

          if (isMounted && match && Array.isArray(match.images)) {
            setGalleryInfo(match);
            setImages(match.images);
          }
        }
      } catch (err) {
        console.error('Failed to load gallery from CMS:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadGallery();
    return () => {
      isMounted = false;
    };
  }, [gallerySlug, customImages]);

  const displayImages = images.slice(0, limit);

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-[#d9238c] animate-pulse">
        Indlæser stemningsgalleri...
      </div>
    );
  }

  if (displayImages.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-[#faf5f9]/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#faf5f9] text-[#9b1b66] text-xs font-semibold uppercase tracking-wider mb-3">
            <i className="fa-regular fa-image" aria-hidden="true"></i>
            <span>Galleri</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1f142e] font-semibold tracking-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-3 text-base text-[#4e3568] leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayImages.map((imgUrl, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => {
                setSelectedIndex(idx);
                setModalOpen(true);
              }}
              className="group relative overflow-hidden rounded-2xl bg-[#faf5f9] aspect-[4/3] text-left focus:outline-none focus:ring-2 focus:ring-[#d9238c] focus:ring-offset-2 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer border border-[#edd5e8]"
              aria-label={`Åbn billede ${idx + 1} i stor visning`}
            >
              <img
                src={getOptimizedImageUrl(imgUrl, { width: 600, height: 450, quality: 80, fit: 'cover' })}
                alt={`Ibjerget stemningsbillede ${idx + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                loading="lazy"
              />
              
              {/* Subtle hover overlay with icon */}
              <div className="absolute inset-0 bg-[#381d6e]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="w-12 h-12 rounded-full bg-white/95 text-[#1f142e] flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <i className="fa-solid fa-expand text-sm" aria-hidden="true"></i>
                </span>
              </div>
            </button>
          ))}
        </div>

        {galleryInfo && galleryInfo.description && (
          <p className="mt-6 text-center text-xs text-[#9b1b66] italic">
            {galleryInfo.description}
          </p>
        )}
      </div>

      {/* Lightbox Modal */}
      <GalleryModal
        isOpen={modalOpen}
        images={images}
        initialIndex={selectedIndex}
        onClose={() => setModalOpen(false)}
      />
    </section>
  );
}
